import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LinkIcon from "@mui/icons-material/Link";
import DeleteIcon from "@mui/icons-material/Delete";
import { BackOfficeUser } from "@/domain/user.interface";
import { UserFormData } from "./validation.schema";
import { TabUser } from "./TabUser";
import { TabRoles } from "./TabRoles";
import { TabAssignOU } from "./TabAssignOU";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/utils/theme";
import { UserStatusEnum } from "@/domain/user.enum";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

import { Program } from "@/domain/programs.interface";
import { Organization } from "@/domain/organizations.interface";

export interface UserModalPresenterProps {
  open: boolean;
  mode: "create" | "edit";
  user: BackOfficeUser | null;
  isLoadingUserData?: boolean;
  formData?: UserFormData;
  activeTab: number;
  isSubmitting: boolean;
  onClose: () => void;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  onSubmit: () => void;
  onResendInvitation?: () => void;
  onDelete?: () => void;
  canResendInvitation: boolean;
  rolesArray: any[];
  isLoadingRoles?: boolean;
  // TabAssignOU props
  programs: Program[];
  isLoadingPrograms: boolean;
  selectedProgram?: string;
  onProgramChange: (programId: string) => void;
  organizations: Organization[];
  isLoadingOrganizations: boolean;
  isAccountAdmin: boolean;
  isSystemAdmin: boolean;
  isSupport: boolean;
  isExemptFromOUValidation: boolean;
}

export const UserModalPresenter: React.FC<UserModalPresenterProps> = ({
  open,
  mode,
  user,
  isLoadingUserData = false,
  formData,
  activeTab,
  isSubmitting,
  onClose,
  onTabChange,
  onSubmit,
  onResendInvitation,
  onDelete,
  canResendInvitation,
  rolesArray,
  isLoadingRoles = false,
  // TabAssignOU props
  programs,
  isLoadingPrograms,
  selectedProgram,
  onProgramChange,
  organizations,
  isLoadingOrganizations,
  isAccountAdmin,
  isSystemAdmin,
  isSupport,
  isExemptFromOUValidation,
}) => {
  const { t } = useTranslation("user");
  const theme = useAppTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOptionsMenuOpen = Boolean(anchorEl);

  const handleOptionsMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOptionsMenuClose = () => {
    setAnchorEl(null);
  };

  const handleResendInvitation = () => {
    handleOptionsMenuClose();
    if (onResendInvitation) {
      onResendInvitation();
    }
  };

  const handleDelete = () => {
    handleOptionsMenuClose();
    if (onDelete) {
      onDelete();
    }
  };

  const isEditMode = mode === "edit";
  const isPending =
    (formData?.status === UserStatusEnum.PENDING ||
      user?.status === UserStatusEnum.PENDING) ??
    false;
  const isOptionsMenuEnabled = isEditMode;

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      {/* Header with title, options menu, and close button */}
      <DialogTitle
        sx={{
          backgroundColor: theme.palette.primary.main,
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 2,
          px: 3,
        }}
      >
        <Typography variant="headlineSmall" sx={{ color: "white" }}>
          {t("title")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, mt: 2 }}>
        <Tabs value={activeTab} onChange={onTabChange}>
          <Tab label={t("tabs.user")} />
          <Tab label={t("tabs.user-roles")} />
          <Tab label={t("tabs.assign-ou")} />
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}
          >
            <IconButton
              size="small"
              onClick={handleOptionsMenuClick}
              disabled={!isOptionsMenuEnabled}
              sx={{
                color: "black",
                opacity: isOptionsMenuEnabled ? 1 : 0.5,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={isOptionsMenuOpen}
              onClose={handleOptionsMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                },
              }}
            >
              <MenuItem
                onClick={handleResendInvitation}
                disabled={!isPending || !canResendInvitation}
              >
                <LinkIcon sx={{ mr: 1.5, fontSize: 20 }} />
                {t("buttons.resend-invitation") || "Resend invitation link"}
              </MenuItem>
              <MenuItem
                onClick={handleDelete}
                sx={{
                  color: "error.main",
                  "&:hover": {
                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                  },
                }}
              >
                <DeleteIcon
                  sx={{ mr: 1.5, fontSize: 20, color: "error.main" }}
                />
                Delete
              </MenuItem>
            </Menu>
          </Box>
        </Tabs>
      </Box>

      <DialogContent sx={{ px: 3, py: 2, mt: 0 }}>
        {isLoadingUserData ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* USER Tab */}
            <TabPanel value={activeTab} index={0}>
              <TabUser editMode={isEditMode} />
            </TabPanel>

            {/* USER ROLE Tab */}
            <TabPanel value={activeTab} index={1}>
              <TabRoles
                rolesArray={rolesArray}
                isSubmitting={isSubmitting}
                clearSubmitting={() => {}}
                isLoadingRoles={isLoadingRoles}
              />
            </TabPanel>

            {/* ASSIGN OU Tab */}
            <TabPanel value={activeTab} index={2}>
              <TabAssignOU
                isEditMode={isEditMode}
                programs={programs}
                isLoadingPrograms={isLoadingPrograms}
                selectedProgram={selectedProgram}
                onProgramChange={onProgramChange}
                organizations={organizations}
                isLoadingOrganizations={isLoadingOrganizations}
                isAccountAdmin={isAccountAdmin}
                isSystemAdmin={isSystemAdmin}
                isSupport={isSupport}
                isExemptFromOUValidation={isExemptFromOUValidation}
              />
            </TabPanel>
          </>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          size="large"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ color: theme.palette.primary.main }}
        >
          {t("buttons.cancel")}
        </Button>
        <Button
          size="large"
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {mode === "create" ? t("buttons.invite") : t("buttons.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
